import { useState, useRef, useEffect, useCallback } from "react";
import MainLayout from "@/components/feature/MainLayout";
import ReportBuilder from "../components/ReportBuilder";

type ReportMode = "daily" | "weekly" | "monthly";

const dailyData = {
  date: "2026-04-10",
  totalRevenue: 28456.80,
  totalUsage: 8234.5,
  newUsers: 12,
  alarmCount: 3,
  hourlyRevenue: [820, 650, 420, 380, 560, 1240, 2180, 2650, 2340, 1980, 1560, 1820, 2100, 1760, 1540, 1680, 1920, 2240, 2560, 2180, 1840, 1560, 1240, 980],
  topUsers: [
    { name: "北京餐饮集团", usage: 456.8, amount: 1575.96 },
    { name: "华联超市朝阳店", usage: 312.4, amount: 1077.78 },
    { name: "金鼎轩酒楼", usage: 289.6, amount: 998.52 },
    { name: "麦当劳望京店", usage: 245.2, amount: 845.94 },
    { name: "海底捞中关村店", usage: 198.4, amount: 684.48 },
  ],
};

const weeklyData = {
  week: "2026年第15周（4月6日-4月12日）",
  totalRevenue: 198560.40,
  totalUsage: 57480.2,
  newUsers: 78,
  alarmCount: 18,
  dailyRevenue: [
    { day: "周一", value: 26580, usage: 7680 },
    { day: "周二", value: 28340, usage: 8210 },
    { day: "周三", value: 27890, usage: 8050 },
    { day: "周四", value: 29120, usage: 8430 },
    { day: "周五", value: 31450, usage: 9100 },
    { day: "周六", value: 28720, usage: 8310 },
    { day: "周日", value: 26460, usage: 7700 },
  ],
  districtRevenue: [
    { name: "朝阳区", value: 58420, pct: 29.4 },
    { name: "海淀区", value: 48650, pct: 24.5 },
    { name: "丰台区", value: 38920, pct: 19.6 },
    { name: "西城区", value: 28340, pct: 14.3 },
    { name: "东城区", value: 24230, pct: 12.2 },
  ],
};

const monthlyData = {
  month: "2026年4月",
  totalRevenue: 856420.50,
  totalUsage: 248650.8,
  activeUsers: 12456,
  arrearsAmount: 45680.20,
  weeklyRevenue: [185600, 212400, 198800, 259620],
  userTypeRevenue: [
    { type: "居民用户", amount: 586420, percent: 68.5 },
    { type: "工商业用户", amount: 198650, percent: 23.2 },
    { type: "工业用户", amount: 71350, percent: 8.3 },
  ],
  monthlyTrend: [725, 685, 715, 745, 785, 805, 775, 815, 835, 845, 825, 856],
};

const aiReportTexts: Record<ReportMode, string> = {
  daily: `【AI日报分析 - 2026-04-10】

一、营收概况
今日营收总额 ¥28,456.80，较昨日增长 8.3%，较上周同期增长 12.1%。用气总量 8,234.5 m³，新增用户 12 人。

二、时段分析
早高峰（07:00-09:00）营收 ¥4,830，占全天 17.0%；晚高峰（18:00-20:00）营收 ¥4,800，占全天 16.9%。
低谷时段（02:00-05:00）营收仅 ¥1,580，建议优化夜间服务资源配置。

三、用户分析
工商业用户贡献营收 ¥18,456，占比 64.8%，其中北京餐饮集团单日用气量最高（456.8 m³）。
居民用户 ¥10,000，占比 35.2%，用气高峰与工商业错峰明显。

四、告警情况
今日发生告警 3 次，均已及时处理。其中压力异常 1 次、设备离线 2 次，无泄漏报警。

五、AI预测
预测明日营收区间：¥29,200 - ¥31,500（置信度 88%）
建议：提前做好早高峰服务保障，关注朝阳区3号调压站压力波动。`,

  weekly: `【AI周报分析 - 2026年第15周】

一、本周营收概况
本周累计营收 ¥198,560.40，较上周增长 6.8%，日均营收 ¥28,366。
周五营收最高（¥31,450），周日最低（¥26,460），工作日与周末差异约 18.8%。

二、区域分析
朝阳区贡献营收最高（¥58,420，占比 29.4%），海淀区次之（¥48,650，占比 24.5%）。
丰台区本周增速最快（+15.2%），建议加大该区域市场开发力度。

三、用气量分析
本周总用气量 57,480.2 m³，日均 8,211 m³。周五用气量最高（9,100 m³），与餐饮业周末备货相关。

四、异常情况
本周共发生告警 18 次，较上周减少 3 次。泄漏报警 2 次（均已处理），设备异常 8 次，欠费预警 8 次。

五、AI预测
预测下周营收：¥205,000 - ¥215,000（置信度 85%）
建议：加强周末工商业用户服务，优化丰台区管网压力调配。`,

  monthly: `【AI月报分析 - 2026年4月】

一、营收总体概况
本月累计营收 ¥856,420.50，同比增长 12.3%，环比增长 5.8%。日均营收 ¥28,547，较上月提升 ¥1,580。

二、营收结构分析
▶ 居民用户：¥586,420（占比68.5%），同比+8.2%
▶ 工商业用户：¥198,650（占比23.2%），同比+18.6%
▶ 工业用户：¥71,350（占比8.3%），同比+15.1%

三、周度趋势
第1周 ¥185,600 → 第2周 ¥212,400（+14.4%）→ 第3周 ¥198,800（-6.4%）→ 第4周 ¥259,620（+30.6%）
第4周营收创近6个月单周新高，主要受工商业复工带动。

四、欠费情况
当前欠费总额 ¥45,680.20，欠费率 5.3%，较上月下降 0.8%。

五、AI预测与建议
预测下月营收区间：¥880,000 - ¥920,000（置信度 85%）
1. 建议加强对欠费用户的短信催缴，预计可回收 ¥32,000+
2. 工商业用户增长势头良好，建议推出大用量优惠套餐
3. 居民用户夏季用气量将下降，建议提前做好营收预期管理`,
};

export default function DailyReportPage() {
  const [mode, setMode] = useState<ReportMode>("daily");
  const [activeTab, setActiveTab] = useState<"report" | "builder">("report");
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiReport, setAiReport] = useState("");
  const [exportModal, setExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState<"pdf" | "excel" | "csv">("pdf");
  const [exporting, setExporting] = useState(false);
  const [exportDone, setExportDone] = useState(false);
  const mainChartRef = useRef<HTMLCanvasElement>(null);
  const subChartRef = useRef<HTMLCanvasElement>(null);
  const trendChartRef = useRef<HTMLCanvasElement>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const drawMainChart = useCallback(() => {
    const canvas = mainChartRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);
    const padL = 40, padR = 10, padT = 20, padB = 28;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;

    let data: number[] = [];
    let labels: string[] = [];
    let highlightIdx = -1;

    if (mode === "daily") {
      data = dailyData.hourlyRevenue;
      labels = Array.from({ length: 24 }, (_, i) => i % 4 === 0 ? `${i}时` : "");
      highlightIdx = 8;
    } else if (mode === "weekly") {
      data = weeklyData.dailyRevenue.map(d => d.value);
      labels = weeklyData.dailyRevenue.map(d => d.day);
      highlightIdx = 4;
    } else {
      data = monthlyData.weeklyRevenue;
      labels = ["第1周", "第2周", "第3周", "第4周"];
      highlightIdx = 3;
    }

    const maxV = Math.max(...data) * 1.15;
    const minV = 0;
    const range = maxV - minV;

    // Grid
    for (let i = 0; i <= 4; i++) {
      const y = padT + (chartH / 4) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.strokeStyle = "rgba(0,0,0,0.05)";
      ctx.lineWidth = 1;
      ctx.stroke();
      const val = maxV - (range / 4) * i;
      ctx.fillStyle = "#9ca3af";
      ctx.font = "9px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(val >= 10000 ? `${(val / 10000).toFixed(1)}万` : val.toFixed(0), padL - 4, y + 3);
    }

    // Bars
    const barW = chartW / data.length;
    data.forEach((v, i) => {
      const barH = ((v - minV) / range) * chartH;
      const x = padL + i * barW + barW * 0.12;
      const bw = barW * 0.76;
      const y = padT + chartH - barH;
      const isHighlight = i === highlightIdx;
      const grad = ctx.createLinearGradient(x, y, x, padT + chartH);
      grad.addColorStop(0, isHighlight ? "#E8531A" : "rgba(232,83,26,0.65)");
      grad.addColorStop(1, isHighlight ? "rgba(232,83,26,0.4)" : "rgba(232,83,26,0.1)");
      ctx.fillStyle = grad;
      ctx.beginPath();
      ctx.roundRect(x, y, bw, barH, 3);
      ctx.fill();
      if (labels[i]) {
        ctx.fillStyle = "#9ca3af";
        ctx.font = "9px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(labels[i], x + bw / 2, H - 8);
      }
    });

    // Trend line overlay
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + i * barW + barW / 2;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "rgba(232,83,26,0.4)";
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.setLineDash([4, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }, [mode]);

  const drawSubChart = useCallback(() => {
    const canvas = subChartRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    if (mode === "daily") {
      // Top users bar chart
      const users = dailyData.topUsers;
      const maxV = users[0].usage;
      const barH = (H - 20) / users.length - 6;
      users.forEach((u, i) => {
        const y = 10 + i * (barH + 6);
        const barW = ((u.usage / maxV) * (W - 120));
        const grad = ctx.createLinearGradient(0, 0, barW, 0);
        grad.addColorStop(0, i === 0 ? "#E8531A" : "rgba(232,83,26,0.5)");
        grad.addColorStop(1, "rgba(232,83,26,0.1)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.roundRect(0, y, barW, barH, 2);
        ctx.fill();
        ctx.fillStyle = "#374151";
        ctx.font = "10px sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(u.name.slice(0, 8), 0, y - 2);
        ctx.fillStyle = "#6b7280";
        ctx.font = "9px sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(`${u.usage}m³`, W, y + barH - 2);
      });
    } else if (mode === "weekly") {
      // District pie-like bar
      const districts = weeklyData.districtRevenue;
      const maxV = districts[0].value;
      const barH = (H - 20) / districts.length - 6;
      districts.forEach((d, i) => {
        const y = 10 + i * (barH + 6);
        const barW = (d.pct / 100) * (W - 80);
        const colors = ["#E8531A", "#f97316", "#fb923c", "#fdba74", "#fed7aa"];
        ctx.fillStyle = colors[i];
        ctx.beginPath();
        ctx.roundRect(0, y, barW, barH, 2);
        ctx.fill();
        ctx.fillStyle = "#374151";
        ctx.font = "10px sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(d.name, 0, y - 2);
        ctx.fillStyle = "#6b7280";
        ctx.font = "9px sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(`${d.pct}%`, W, y + barH - 2);
      });
    } else {
      // User type donut-like bars
      const types = monthlyData.userTypeRevenue;
      const barH = (H - 20) / types.length - 8;
      types.forEach((t, i) => {
        const y = 10 + i * (barH + 8);
        const barW = (t.percent / 100) * (W - 80);
        const colors = ["#E8531A", "#06b6d4", "#f59e0b"];
        ctx.fillStyle = colors[i];
        ctx.beginPath();
        ctx.roundRect(0, y, barW, barH, 3);
        ctx.fill();
        ctx.fillStyle = "#374151";
        ctx.font = "10px sans-serif";
        ctx.textAlign = "left";
        ctx.fillText(t.type, 0, y - 2);
        ctx.fillStyle = "#6b7280";
        ctx.font = "9px sans-serif";
        ctx.textAlign = "right";
        ctx.fillText(`${t.percent}%`, W, y + barH - 2);
      });
    }
  }, [mode]);

  const drawTrendChart = useCallback(() => {
    const canvas = trendChartRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const W = canvas.width, H = canvas.height;
    ctx.clearRect(0, 0, W, H);

    const data = monthlyData.monthlyTrend;
    const labels = ["1月", "2月", "3月", "4月", "5月", "6月", "7月", "8月", "9月", "10月", "11月", "12月"];
    const padL = 36, padR = 10, padT = 12, padB = 20;
    const chartW = W - padL - padR;
    const chartH = H - padT - padB;
    const minV = Math.min(...data) - 30;
    const maxV = Math.max(...data) + 30;
    const range = maxV - minV;

    // Grid
    for (let i = 0; i <= 3; i++) {
      const y = padT + (chartH / 3) * i;
      ctx.beginPath();
      ctx.moveTo(padL, y);
      ctx.lineTo(W - padR, y);
      ctx.strokeStyle = "rgba(0,0,0,0.05)";
      ctx.lineWidth = 1;
      ctx.stroke();
      const val = maxV - (range / 3) * i;
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(`${val.toFixed(0)}`, padL - 3, y + 3);
    }

    // Area fill
    const grad = ctx.createLinearGradient(0, padT, 0, padT + chartH);
    grad.addColorStop(0, "rgba(232,83,26,0.2)");
    grad.addColorStop(1, "rgba(232,83,26,0)");
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.lineTo(padL + chartW, padT + chartH);
    ctx.lineTo(padL, padT + chartH);
    ctx.closePath();
    ctx.fillStyle = grad;
    ctx.fill();

    // Line
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    });
    ctx.strokeStyle = "#E8531A";
    ctx.lineWidth = 2;
    ctx.lineJoin = "round";
    ctx.stroke();

    // Dots + labels
    data.forEach((v, i) => {
      const x = padL + (i / (data.length - 1)) * chartW;
      const y = padT + chartH - ((v - minV) / range) * chartH;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fillStyle = "#E8531A";
      ctx.fill();
      ctx.fillStyle = "#9ca3af";
      ctx.font = "8px sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(labels[i], x, H - 4);
    });
  }, []);

  useEffect(() => {
    drawMainChart();
    drawSubChart();
    drawTrendChart();
  }, [mode, drawMainChart, drawSubChart, drawTrendChart]);

  const generateAIReport = () => {
    setAiGenerating(true);
    setAiReport("");
    const text = aiReportTexts[mode];
    let i = 0;
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => {
      if (i < text.length) {
        setAiReport(text.slice(0, i + 1));
        i += 3;
      } else {
        setAiReport(text);
        if (timerRef.current) clearInterval(timerRef.current);
        setAiGenerating(false);
      }
    }, 15);
  };

  const handleExport = () => {
    setExporting(true);
    setTimeout(() => {
      setExporting(false);
      setExportDone(true);
      setExportModal(false);
      setTimeout(() => setExportDone(false), 3000);
    }, 2000);
  };

  useEffect(() => () => { if (timerRef.current) clearInterval(timerRef.current); }, []);

  const modeLabels: Record<ReportMode, string> = { daily: "日报表", weekly: "周报表", monthly: "月报表" };
  const currentStats = mode === "daily"
    ? [
        { label: "今日营收", value: `¥${dailyData.totalRevenue.toLocaleString()}`, icon: "ri-money-cny-circle-line", color: "text-[#E8531A]", bg: "bg-orange-50", trend: "+8.3%", up: true },
        { label: "今日用气量", value: `${dailyData.totalUsage} m³`, icon: "ri-gas-station-line", color: "text-teal-600", bg: "bg-teal-50", trend: "+5.1%", up: true },
        { label: "新增用户", value: `${dailyData.newUsers} 人`, icon: "ri-user-add-line", color: "text-green-600", bg: "bg-green-50", trend: "+2", up: true },
        { label: "告警次数", value: `${dailyData.alarmCount} 次`, icon: "ri-alarm-warning-line", color: "text-amber-600", bg: "bg-amber-50", trend: "-1", up: false },
      ]
    : mode === "weekly"
    ? [
        { label: "本周营收", value: `¥${(weeklyData.totalRevenue / 10000).toFixed(1)}万`, icon: "ri-money-cny-circle-line", color: "text-[#E8531A]", bg: "bg-orange-50", trend: "+6.8%", up: true },
        { label: "本周用气量", value: `${(weeklyData.totalUsage / 10000).toFixed(1)}万m³`, icon: "ri-gas-station-line", color: "text-teal-600", bg: "bg-teal-50", trend: "+4.2%", up: true },
        { label: "新增用户", value: `${weeklyData.newUsers} 人`, icon: "ri-user-add-line", color: "text-green-600", bg: "bg-green-50", trend: "+12", up: true },
        { label: "告警次数", value: `${weeklyData.alarmCount} 次`, icon: "ri-alarm-warning-line", color: "text-amber-600", bg: "bg-amber-50", trend: "-3", up: false },
      ]
    : [
        { label: "月度营收", value: `¥${(monthlyData.totalRevenue / 10000).toFixed(1)}万`, icon: "ri-money-cny-circle-line", color: "text-[#E8531A]", bg: "bg-orange-50", trend: "+12.3%", up: true },
        { label: "月度用气量", value: `${(monthlyData.totalUsage / 10000).toFixed(1)}万m³`, icon: "ri-gas-station-line", color: "text-teal-600", bg: "bg-teal-50", trend: "+8.7%", up: true },
        { label: "活跃用户", value: `${monthlyData.activeUsers.toLocaleString()}`, icon: "ri-user-3-line", color: "text-green-600", bg: "bg-green-50", trend: "+234", up: true },
        { label: "欠费金额", value: `¥${(monthlyData.arrearsAmount / 10000).toFixed(1)}万`, icon: "ri-error-warning-line", color: "text-red-600", bg: "bg-red-50", trend: "-2.1%", up: false },
      ];

  return (
    <MainLayout>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-bold text-gray-900">营收报表</h2>
            <p className="text-xs text-gray-400 mt-0.5">日/周/月多维度营收数据分析，AI智能生成报告</p>
          </div>
          <div className="flex gap-2">
            {exportDone && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 px-3 py-1.5 rounded-lg text-xs">
                <i className="ri-check-double-line"></i>导出成功
              </div>
            )}
            <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
              {(["report", "builder"] as const).map(t => (
                <button key={t} onClick={() => setActiveTab(t)} className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${activeTab === t ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500"}`}>
                  {t === "report" ? "标准报表" : "自定义报表"}
                </button>
              ))}
            </div>
          </div>
        </div>

        {activeTab === "report" && (
          <>
            {/* Mode switch */}
            <div className="flex items-center justify-between">
              <div className="flex gap-1 bg-gray-100 rounded-xl p-1">
                {(["daily", "weekly", "monthly"] as ReportMode[]).map(m => (
                  <button
                    key={m}
                    onClick={() => { setMode(m); setAiReport(""); }}
                    className={`px-5 py-2 rounded-lg text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${mode === m ? "bg-white text-[#E8531A] shadow-sm" : "text-gray-500 hover:text-gray-700"}`}
                  >
                    {modeLabels[m]}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setExportModal(true)}
                  className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                >
                  <i className="ri-download-2-line text-[#E8531A]"></i>导出报表
                </button>
                <button className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                  <i className="ri-printer-line"></i>打印
                </button>
              </div>
            </div>

            {/* Stats cards */}
            <div className="grid grid-cols-4 gap-3">
              {currentStats.map((s, i) => (
                <div key={i} className={`${s.bg} rounded-xl p-4`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-white">
                      <i className={`${s.icon} ${s.color} text-base`}></i>
                    </div>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${s.up ? "bg-green-100 text-green-600" : "bg-red-100 text-red-500"}`}>{s.trend}</span>
                  </div>
                  <div className={`text-xl font-bold ${s.color}`}>{s.value}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>

            {/* Charts row */}
            <div className="grid grid-cols-3 gap-4">
              {/* Main chart */}
              <div className="col-span-2 bg-white rounded-xl border border-gray-100 p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">
                      {mode === "daily" ? "分时营收趋势（元）" : mode === "weekly" ? "日度营收趋势（元）" : "周度营收趋势（元）"}
                    </h3>
                    <p className="text-xs text-gray-400 mt-0.5">
                      {mode === "daily" ? "今日 · 2026-04-10" : mode === "weekly" ? "本周 · 第15周" : "本月 · 2026年4月"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-2 rounded bg-[#E8531A]"></div>
                      <span className="text-xs text-gray-400">营收</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <div className="w-3 h-0.5 bg-[#E8531A]/40" style={{ borderTop: "2px dashed rgba(232,83,26,0.4)" }}></div>
                      <span className="text-xs text-gray-400">趋势</span>
                    </div>
                  </div>
                </div>
                <canvas ref={mainChartRef} width={600} height={200} className="w-full" />
              </div>

              {/* Sub chart */}
              <div className="bg-white rounded-xl border border-gray-100 p-5">
                <h3 className="text-sm font-semibold text-gray-800 mb-1">
                  {mode === "daily" ? "TOP5用气用户" : mode === "weekly" ? "区域营收分布" : "用户类型占比"}
                </h3>
                <p className="text-xs text-gray-400 mb-4">
                  {mode === "daily" ? "按用气量排序" : mode === "weekly" ? "各区域贡献占比" : "三类用户营收结构"}
                </p>
                <canvas ref={subChartRef} width={280} height={200} className="w-full" />
              </div>
            </div>

            {/* Monthly trend (always show) */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-sm font-semibold text-gray-800">全年营收趋势（万元）</h3>
                  <p className="text-xs text-gray-400 mt-0.5">2026年1月-12月营收走势</p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-gray-500">年度累计：<span className="font-bold text-[#E8531A]">¥9,406万</span></div>
                  <div className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">同比 +11.2%</div>
                </div>
              </div>
              <canvas ref={trendChartRef} width={900} height={120} className="w-full" />
            </div>

            {/* AI Report */}
            <div className="bg-white rounded-xl border border-gray-100 p-5">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-orange-50">
                    <i className="ri-robot-2-line text-[#E8531A] text-sm"></i>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800">AI智能分析报告</h3>
                    <p className="text-xs text-gray-400">{modeLabels[mode]} · 自动生成</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={generateAIReport}
                    disabled={aiGenerating}
                    className="flex items-center gap-2 bg-[#E8531A] text-white px-4 py-2 rounded-lg text-xs font-medium cursor-pointer hover:bg-[#d44a15] disabled:opacity-60 whitespace-nowrap"
                  >
                    {aiGenerating ? <><i className="ri-loader-4-line animate-spin"></i>生成中</> : <><i className="ri-robot-2-line"></i>AI生成报告</>}
                  </button>
                  {aiReport && (
                    <>
                      <button onClick={() => setExportModal(true)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                        <i className="ri-file-pdf-line text-red-500"></i>导出PDF
                      </button>
                      <button onClick={() => setExportModal(true)} className="flex items-center gap-1.5 border border-gray-200 text-gray-600 px-3 py-2 rounded-lg text-xs cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                        <i className="ri-file-excel-2-line text-green-600"></i>导出Excel
                      </button>
                    </>
                  )}
                </div>
              </div>
              {aiReport ? (
                <div className="bg-gray-50 rounded-xl p-5 text-xs text-gray-700 leading-relaxed whitespace-pre-line font-mono">
                  {aiReport}
                  {aiGenerating && <span className="animate-pulse text-[#E8531A]">|</span>}
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-10 text-center">
                  <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-3">
                    <i className="ri-robot-2-line text-[#E8531A] text-3xl"></i>
                  </div>
                  <p className="text-sm text-gray-400 mb-1">点击「AI生成报告」按钮</p>
                  <p className="text-xs text-gray-300">AI将自动分析{modeLabels[mode]}数据，生成专业分析报告</p>
                  <div className="mt-4 flex items-center justify-center gap-4">
                    {["多维度数据分析", "趋势预测", "智能建议"].map((f, i) => (
                      <div key={i} className="flex items-center gap-1.5 text-xs text-gray-400">
                        <i className="ri-check-line text-[#E8531A]"></i>{f}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab === "builder" && <ReportBuilder />}
      </div>

      {/* Export Modal */}
      {exportModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => !exporting && setExportModal(false)}>
          <div className="bg-white rounded-2xl p-6 w-[480px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-base font-bold text-gray-900">导出{modeLabels[mode]}</h3>
              {!exporting && (
                <button onClick={() => setExportModal(false)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                  <i className="ri-close-line text-gray-500"></i>
                </button>
              )}
            </div>
            {exporting ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 rounded-full bg-orange-50 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-loader-4-line animate-spin text-[#E8531A] text-3xl"></i>
                </div>
                <div className="text-sm font-medium text-gray-700 mb-1">正在生成 {exportFormat.toUpperCase()} 文件...</div>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-4">
                  <div className="bg-[#E8531A] h-2 rounded-full animate-pulse" style={{ width: "65%" }}></div>
                </div>
              </div>
            ) : (
              <>
                <div className="mb-5">
                  <label className="text-xs text-gray-500 mb-3 block">选择导出格式</label>
                  <div className="grid grid-cols-3 gap-3">
                    {[
                      { key: "pdf", label: "PDF文档", icon: "ri-file-pdf-2-line", color: "text-red-500", desc: "含图表和报告" },
                      { key: "excel", label: "Excel表格", icon: "ri-file-excel-2-line", color: "text-green-600", desc: "原始数据表格" },
                      { key: "csv", label: "CSV数据", icon: "ri-file-text-line", color: "text-gray-600", desc: "纯数据格式" },
                    ].map(fmt => (
                      <button
                        key={fmt.key}
                        onClick={() => setExportFormat(fmt.key as typeof exportFormat)}
                        className={`flex flex-col items-center gap-2 p-4 rounded-xl border-2 transition-colors cursor-pointer ${exportFormat === fmt.key ? "border-[#E8531A] bg-orange-50" : "border-gray-100 hover:border-gray-200"}`}
                      >
                        <i className={`${fmt.icon} text-2xl ${fmt.color}`}></i>
                        <div className="text-center">
                          <div className={`text-xs font-semibold ${exportFormat === fmt.key ? "text-[#E8531A]" : "text-gray-700"}`}>{fmt.label}</div>
                          <div className="text-[10px] text-gray-400 mt-0.5">{fmt.desc}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="mb-5 space-y-2">
                  <label className="text-xs text-gray-500 block">导出内容</label>
                  {["报表数据（含所有维度）", "AI智能分析报告", "图表截图（仅PDF）", "数据汇总统计"].map((opt, i) => (
                    <label key={i} className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" defaultChecked className="accent-[#E8531A]" />
                      <span className="text-sm text-gray-700">{opt}</span>
                    </label>
                  ))}
                </div>
                <div className="mb-5">
                  <label className="text-xs text-gray-500 mb-1.5 block">文件名称</label>
                  <input type="text" defaultValue={`${modeLabels[mode]}_2026-04-10`} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E8531A]" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setExportModal(false)} className="flex-1 border border-gray-200 text-gray-600 py-2.5 rounded-xl text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">取消</button>
                  <button onClick={handleExport} className="flex-1 bg-[#E8531A] text-white py-2.5 rounded-xl text-sm font-medium cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                    <i className="ri-download-2-line mr-1"></i>确认导出 {exportFormat.toUpperCase()}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </MainLayout>
  );
}
